import type { ExtractedClientItemDto } from '../../lib/confirmedOrderApi';

/**
 * Which line of the ship's PO (or later email) the server matched to one line of the order, or
 * undefined.
 *
 * D81 rule 2 (owner, 2026-09-28): the server decides it, once (confirmedOrderShipPo.service
 * `matchShipLines`), and stores the quoted line's number on the ship's line. This READS that
 * number against the order line's, resolved the way every writer stores it (`lineNumber ?? index +
 * 1`, TECH_DEBT #299). It used to match names a second time too, on the first eight letters, so the
 * screen and the server could disagree about the same line.
 */
export function emailLineFor(
  orderLine: { lineNumber?: number | null },
  index: number,
  extracted: ExtractedClientItemDto[],
): ExtractedClientItemDto | undefined {
  const lineNumber = orderLine.lineNumber ?? index + 1;
  return extracted.find((e) => e.matchedQuotationLineNumber === lineNumber);
}

export interface ReconcileRow {
  [key: string]: unknown;
  quantity: number | null;
  _quotedQty: number | null;
  _clientQty: number | null;
  _keep: boolean;
}

/**
 * D81 rule 3 (owner, 2026-09-28): whether one order line starts TICKED in Review & Finalise.
 *
 * - A line the ship wrote starts ticked, unless it asks for none of it (quantity 0).
 * - A line the ship did not write depends on what was read: from a PO DOCUMENT (an attachment or
 *   an uploaded file) the read is the ship's whole order, so the line was left out and starts
 *   unticked; from the email's TEXT the read is only what changes, so the line stays as it is and
 *   starts ticked. Nothing read at all changes nothing.
 *
 * `readFrom` is stored by the server on every line it read (ExtractedClientItemSchema.readFrom); a
 * line read before it was stored has none and counts as text.
 */
export function startsTicked(match: ExtractedClientItemDto | undefined, extracted: ExtractedClientItemDto[]): boolean {
  if (match) return match.quantity !== 0;
  return !extracted.some((e) => e.readFrom === 'document');
}

/**
 * The Review & Finalise table, seeded from what the ship's PO or email wrote: each order line
 * with the ship's quantity beside it, ticked by `startsTicked`. The same for the confirmation's
 * own read and for a later email (D45.3).
 */
export function seedReconcileLines(orderLines: any[], extracted: ExtractedClientItemDto[]): ReconcileRow[] {
  return orderLines.map((line, index) => {
    const match = emailLineFor(line, index, extracted);
    return {
      ...line,
      _quotedQty: line.quantity ?? null,
      _clientQty: match?.quantity ?? null,
      _keep: startsTicked(match, extracted),
      quantity: match?.quantity ?? line.quantity ?? null,
    };
  });
}

/** The lines a later email wrote that match no line of the order — they cannot be applied here. */
export function emailLinesNotOnOrder(orderLines: any[], extracted: ExtractedClientItemDto[]): ExtractedClientItemDto[] {
  const matched = new Set<ExtractedClientItemDto>();
  orderLines.forEach((line, index) => {
    const match = emailLineFor(line, index, extracted);
    if (match) matched.add(match);
  });
  return extracted.filter((e) => !matched.has(e));
}

/**
 * D81 — the Review & Finalise rows as the one request the server accepts: per stored line (by
 * POSITION — the rows are seeded from the order's `items` in stored order), keep or remove and the
 * new quantity. Nothing else about a line is sent; the server rebuilds it from the stored line.
 *
 * `blankLines` names every KEPT row with no quantity above 0 — blank is never 0 (invariant 8), so
 * the screen blocks it (the server refuses it too, ORDER_LINE_QUANTITY_INVALID). A row left at
 * its stored quantity (`_quotedQty`) is not blocked: it is not new input, even a legacy stored 0.
 */
export function quantityChangesFrom(rows: ReconcileRow[]): {
  changes: Array<{ index: number; quantity: number | null; keep: boolean }>;
  blankLines: number[];
} {
  const blankLines: number[] = [];
  const changes = rows.map((row, index) => {
    const quantity = typeof row.quantity === 'number' && Number.isFinite(row.quantity) ? row.quantity : null;
    // A line left at its stored quantity is not new input (the server returns it untouched).
    const unchanged = quantity !== null && quantity === row._quotedQty;
    if (row._keep && !unchanged && !(quantity !== null && quantity > 0)) {
      blankLines.push(Number((row as { lineNumber?: number | null }).lineNumber ?? index + 1));
    }
    return { index, quantity, keep: !!row._keep };
  });
  return { changes, blankLines };
}
